/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ScreenDestinationUiAction, UiDestinationType } from 'client-code/homeshortcut';

import { QuickMenuShortcutInteractor } from '../../../core/interactors/QuickMenuShortcutInteractor';
import { Locale, LocaleKeys } from '../../../core/locale';
import { ScreenControllerTypes } from '../../../core/navigation';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { ListTypes } from '../../../core/utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { disableGhostMode } from '../../guide/guidehighlight/guidehighlight.reducers';
import { onKeyDownList, onKeyUpList, onMouseOverList } from '../../list/list.reducers';
import {
    onKeyBackExitConfirmationOverlay,
    onKeyOkExitConfirmationOverlay,
    onMouseClickExitConfirmationOverlay,
    showExitConfirmationOverlayDialog,
} from '../../overlay/exitconfirmationoverlay/exitconfirmationoverlay.reducers';
import {
    onKeyBackNoNetworkOverlay,
    onKeyOkNoNetworkOverlay,
    onMouseClickNoNetworkOverlay,
} from '../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import {
    setScreenController,
    setScreenTaskController,
    showMainSettingsScreen,
    showMyShowsScreen,
} from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { setIsSidebarNavigationMenuActiveAction } from '../sidebarnavigationmenu.actions';
import { MOUSE_TRAP_ID, VALUE_SEPERATOR } from '../sidebarnavigationmenu.constants';
import { hideSidebarNavigationMenu } from '../sidebarnavigationmenu.reducers';

const onKeyOkDefaultSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { position } = getState().list[ListTypes.SIDEBAR_MENU_ITEMS];
    const quickMenuService = new QuickMenuShortcutInteractor();
    const quickMenuShortcut = quickMenuService.getCachedQuickMenuShortcuts();

    const screenAction = quickMenuShortcut[position].uiAction as ScreenDestinationUiAction;
    const uiDestination: UiDestinationType | undefined = screenAction.destination;

    switch (uiDestination) {
        case UiDestinationType.Guide:
            dispatch(setScreenController(ScreenControllerTypes.GUIDE));
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
            dispatch(disableGhostMode());
            break;
        case UiDestinationType.MyShows:
            if (NetworkManager.isNetworkDisconnected())
                dispatch(
                    showWhisper({
                        message: Locale.getString(
                            LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION,
                        ),
                        iconType: WhisperTypes.INFO,
                    }),
                );
            else dispatch(showMyShowsScreen());
            break;
        case UiDestinationType.Settings:
            dispatch(showMainSettingsScreen());
            break;

        default:
    }
    dispatch(setIsSidebarNavigationMenuActiveAction(false));
};

const onMouseClickDefaultSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;
    if (value !== MOUSE_TRAP_ID) dispatch(hideSidebarNavigationMenu());
    const listType = value.split(VALUE_SEPERATOR)[1];
    if (listType !== ListTypes.SIDEBAR_MENU_ITEMS) return;

    dispatch(onKeyOkDefaultSidebarNavigationMenu());
};

export const onKeyBackSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(showExitConfirmationOverlayDialog());
            break;

        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onKeyBackExitConfirmationOverlay());
            break;

        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyBackNoNetworkOverlay());
            break;

        default:
    }
};

export const onKeyRightSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(hideSidebarNavigationMenu());
            break;
        default:
    }
};

export const onKeyUpSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(onKeyUpList(ListTypes.SIDEBAR_MENU_ITEMS));
            break;
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onKeyUpOverlayDialog());
            break;

        default:
    }
};

export const onKeyDownSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(onKeyDownList(ListTypes.SIDEBAR_MENU_ITEMS));
            break;
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onKeyDownOverlayDialog());
            break;

        default:
    }
};

export const onKeyOkSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(onKeyOkDefaultSidebarNavigationMenu());
            break;
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onKeyOkExitConfirmationOverlay());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyOkNoNetworkOverlay());
            break;

        default:
    }
};

export const onMouseClickSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(onMouseClickDefaultSidebarNavigationMenu());
            break;

        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onMouseClickExitConfirmationOverlay());
            break;

        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseClickNoNetworkOverlay());
            break;

        default:
    }
};

export const onMouseOutSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseOutOverlayDialogOperations());
            break;

        default:
    }
};

export const onMouseOverSidebarNavigationMenu = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS:
            dispatch(onMouseOverList());
            break;
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseOverOverlayDialogOperations());
            break;

        default:
    }
};
